import { useFetchNotifications } from "@sushicorp/services";
import { usePutNotificationsMarkAsRead } from "@sushicorp/services";
import { removeDuplicates } from "@sushicorp/utils";
import dayjs from "dayjs";
import React, { useMemo, useEffect } from "react";
import "dayjs/locale/es";

import { getDateOfNotifications } from "./NotificationList.helpers";
import { sortNotificationsByDate } from "./NotificationList.helpers";
import { getNotificationsByDate } from "./NotificationList.helpers";
import { getUnreadNotificationsId } from "./NotificationList.helpers";
import Styles from "./NotificationList.styles";
import { NotificationListProps as Props } from "./NotificationList.types";
import ErrorBlock from "components/global/ErrorBlock/ErrorBlock";
import Notification from "components/profileNotifications/Notification/Notification";
import NotificationPlaceholder from "components/profileNotifications/Notification/Notification.placeholder";
import useAuth from "contexts/auth/auth.context.hooks";

import NotificationSVG from "../../../../public/assets/images/notifications-large.svg";

const NotificationList: React.FC<Props> = props => {
  const auth = useAuth();
  const { uid } = auth;
  const { data, isLoading, isError, isSuccess } = useFetchNotifications(auth);
  const { mutate } = usePutNotificationsMarkAsRead(auth);
  const notifications = useMemo(
    () => sortNotificationsByDate(data ?? []),
    [data]
  );
  const dates = useMemo(
    () => getDateOfNotifications(notifications),
    [notifications]
  );
  const uniqueDates = useMemo(() => removeDuplicates(dates), [dates]);
  const notificationsByDate = useMemo(
    () => getNotificationsByDate(uniqueDates, notifications),
    [notifications, uniqueDates]
  );
  const unreadNotifications = useMemo(
    () => getUnreadNotificationsId(notifications),
    [notifications]
  );
  const isEmpty = isSuccess && notifications.length === 0;

  const loadingNode = (
    <div className="NotificationList__placeholder">
      <div className="NotificationList__placeholder__date loading-shine" />
      <NotificationPlaceholder className="NotificationList__placeholder__notification" />
      <NotificationPlaceholder className="NotificationList__placeholder__notification" />
      <NotificationPlaceholder className="NotificationList__placeholder__notification" />
    </div>
  );

  const emptyNode = (
    <div className="NotificationList__empty">
      <div className="NotificationList__empty__icon">
        <NotificationSVG className="NotificationList__notification-icon" />
      </div>
      <p className="NotificationList__empty__title">
        No tienes ninguna notificación
      </p>
      <p className="NotificationList__empty__description">
        Aquí se mostrarán las notificaciones de tu pedido
      </p>
    </div>
  );

  const errorNode = (
    <ErrorBlock
      className="NotificationList__error-block"
      queryKey={[uid, "notifications"]}
    />
  );

  const notificationsNode = (
    <>
      {notificationsByDate?.map((notifications, index) => {
        return (
          <div key={index}>
            <p className="NotificationList__date">
              {dayjs(notifications?.[0]?.creationDate)
                .locale("es")
                .format("DD MMMM")}
            </p>
            {notifications?.map(notification => {
              return (
                <Notification
                  key={notification.id}
                  className="NotificationList__notification"
                  notification={notification}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );

  useEffect(() => {
    if (!unreadNotifications.length) return;
    mutate(unreadNotifications);
  }, [mutate, unreadNotifications]);

  return (
    <Styles className="NotificationList">
      {isLoading ? loadingNode : null}
      {isError ? errorNode : null}
      {isSuccess && !isEmpty ? notificationsNode : null}
      {isSuccess && isEmpty ? emptyNode : null}
    </Styles>
  );
};

NotificationList.defaultProps = {};

export default NotificationList;
